import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import {
  $createTableNodeWithDimensions,
  INSERT_TABLE_COMMAND,
  InsertTableCommandPayload,
  TableCellNode,
  TableNode,
  TableRowNode,
} from '@lexical/table';
import { $insertNodeToNearestRoot } from '@lexical/utils';
import {
  $getPreviousSelection,
  $getSelection,
  $isTextNode,
  CommandListener,
  CommandListenerPriority,
  CommandListenerPriorityBefore,
  createEditor,
  LexicalCommand,
  LexicalEditor,
} from 'lexical';

import { preserveParagraphSerialization } from '../lexical/nodes/MentionNode';
import { ImageNode } from './nodes/ImageNode';

const tableInsertPatch = Symbol.for('@main/ui/table-insert');

preserveParagraphSerialization();
preserveTableInsertionWithoutSelection();

// If the user has not clicked in the editor, Lexical 0.50 ignores Insert. Add the table at the end instead.
function preserveTableInsertionWithoutSelection() {
  const prototype = Object.getPrototypeOf(createEditor()) as LexicalEditor;
  if (Reflect.get(prototype, tableInsertPatch) === true) return;

  const registerCommand = prototype.registerCommand;
  prototype.registerCommand = function compatibleRegisterCommand<Payload>(
    command: LexicalCommand<Payload>,
    listener: CommandListener<Payload>,
    priority: CommandListenerPriority | CommandListenerPriorityBefore,
  ) {
    const register = registerCommand as unknown as (
      this: LexicalEditor,
      command: LexicalCommand<Payload>,
      listener: CommandListener<Payload>,
      priority: CommandListenerPriority | CommandListenerPriorityBefore,
    ) => () => void;

    if ((command as unknown) !== INSERT_TABLE_COMMAND) {
      return register.call(this, command, listener, priority);
    }

    const compatibleListener: CommandListener<Payload> = (payload, editor) => {
      if (listener(payload, editor)) return true;
      if (($getSelection() || $getPreviousSelection()) !== null) return false;

      const { rows, columns, includeHeaders } = payload as InsertTableCommandPayload;
      const tableNode = $createTableNodeWithDimensions(
        Number(rows),
        Number(columns),
        includeHeaders,
      );
      $insertNodeToNearestRoot(tableNode);
      const firstDescendant = tableNode.getFirstDescendant();
      if ($isTextNode(firstDescendant)) firstDescendant.select();
      return true;
    };

    return register.call(this, command, compatibleListener, priority);
  };
  Object.defineProperty(prototype, tableInsertPatch, { value: true });
}

export const WysiwygBaseNodes = [
  CodeHighlightNode,
  CodeNode,
  AutoLinkNode,
  LinkNode,
  ListItemNode,
  ListNode,
  HorizontalRuleNode,
  HeadingNode,
  QuoteNode,
  TableNode,
  TableRowNode,
  TableCellNode,
];

export const WysiwygImageNodes = [ImageNode];

export const WysiwygNodes = [...WysiwygBaseNodes, ...WysiwygImageNodes];

export function getWysiwygNodes({ images = false }: { images?: boolean } = {}) {
  return images ? WysiwygNodes : WysiwygBaseNodes;
}
